import React, { Fragment, useState, useEffect, useCallback } from 'react';
import {
    Box, Stack, Typography, Paper, Divider, Chip, Button, IconButton, FormControlLabel, Switch, Tooltip,
    Accordion, AccordionSummary, AccordionDetails,
    Table, TableHead, TableBody, TableRow, TableCell, TableContainer,
    Dialog, DialogTitle, DialogContent, DialogActions,
    ToggleButton, ToggleButtonGroup,
    CircularProgress, Alert, Collapse, Popover,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import ArchiveIcon from '@mui/icons-material/Archive';
import UnarchiveIcon from '@mui/icons-material/Unarchive';
import LocalOfferIcon from '@mui/icons-material/LocalOffer';
import CreditScoreIcon from '@mui/icons-material/CreditScore';
import TuneIcon from '@mui/icons-material/Tune';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong';
import CreateApi from '../../../shared/Settings/appSettings';
import { StyledTextboxInput } from '../../../shared/Components/StyledTextboxInput';
import { useTBContext } from '../../Contexts/useTBStates';
import { useAuth } from 'react-oidc-context';
import type {
    UserCreditsDiscounts, CreditDiscountBucket, TourCreditDiscountBucket,
    CreditEntry, CreditTransaction, DiscountEntry, AddCreditPayload, AdjustCreditPayload, AddDiscountPayload, UpdateDiscountPayload,
    CloseExpiredCreditPayload,
} from '@moment-golf/types';

interface CreditsDiscountsSectionProps {
    appDims: any;
    compHeights: any;
}

type Scope = 'momentGolf' | 'tour';

const EMPTY_BUCKET: CreditDiscountBucket = { credits: [], discounts: [] };

// ── Helpers ──────────────────────────────────────────────────────────────────
const formatCurrency = (val: number) =>
    new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(val);

/** UTC date + time for credit transaction audit trail. */
const formatDateTimeUTC = (val: any) => {
    if (!val) return '—';
    try {
        return `${new Date(val).toLocaleString('en-US', {
            timeZone: 'UTC',
            year: 'numeric',
            month: 'short',
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit',
            hour12: false,
        })} UTC`;
    } catch { return String(val); }
};

const formatDate = (val: any) => {
    if (!val) return '—';
    try { return new Date(val).toLocaleDateString('en-US', { timeZone: 'UTC' }); } catch { return String(val); }
};

const toUTCDateInput = (val: any) => {
    if (!val) return '';
    try { return new Date(val).toISOString().slice(0, 10); } catch { return ''; }
};

/** Face value credited = sum of issued + adjusted transactions (mirrors rewards lifetimeEarned for adjustments). */
const creditGrantedFaceTotal = (entry: CreditEntry): number => {
    const txs = entry.transactions ?? [];
    let sum = 0;
    let counted = false;
    for (const t of txs) {
        if (t.type === 'issued' || t.type === 'adjusted') {
            sum += t.amount;
            counted = true;
        }
    }
    return counted ? sum : entry.totalAmount;
};

/** Aligns ~1h grace after nominal expiry (checkout). UTC-oriented; server may differ for date-only + local TZ. */
const CREDIT_CLOSEOUT_GRACE_MS = 60 * 60 * 1000;
const isLikelyExpiredForCloseout = (expiresAt: unknown): boolean => {
    if (expiresAt == null) return false;
    const raw = expiresAt instanceof Date ? expiresAt.toISOString() : String(expiresAt).trim();
    let deadline: number;
    if (/^\d{4}-\d{2}-\d{2}$/.test(raw)) {
        deadline = new Date(`${raw}T23:59:59.999Z`).getTime() + CREDIT_CLOSEOUT_GRACE_MS;
    } else {
        deadline = new Date(raw).getTime() + CREDIT_CLOSEOUT_GRACE_MS;
    }
    return Date.now() >= deadline;
};

const SectionLabel = ({ label, factor }: { label: string; factor: number }) => (
    <Typography
        variant="caption"
        color="primary.main"
        sx={{ fontWeight: 700, textTransform: 'uppercase', letterSpacing: 1, fontSize: 10 * factor }}
    >
        {label}
    </Typography>
);

// ── Credits sub-panel ────────────────────────────────────────────────────────
const CREDIT_TX_CHIP_COLOR: Record<CreditTransaction['type'], 'default' | 'primary' | 'success' | 'warning' | 'error'> = {
    issued: 'primary',
    adjusted: 'warning',
    redeemed: 'default',
    expired: 'error',
};

const CreditsPanel = ({
    credits, factor, onAdd, onAdjustCredit, onCloseExpiredCredit,
}: {
    credits: CreditEntry[];
    factor: number;
    onAdd: () => void;
    onAdjustCredit?: (entry: CreditEntry) => void;
    onCloseExpiredCredit?: (entry: CreditEntry) => void;
}) => {
    const [expanded, setExpanded] = useState<Record<string, boolean>>({});
    const [checkoutPopover, setCheckoutPopover] = useState<{
        anchorEl: HTMLElement;
        pendingCheckoutId: string;
    } | null>(null);
    const toggleExpand = (creditID: string) => {
        setExpanded(prev => ({ ...prev, [creditID]: !prev[creditID] }));
    };

    const balanceTotal = credits.filter(c => c.isActive).reduce((sum, c) => sum + c.remainingAmount, 0);
    const issuedTotal = credits.reduce((sum, c) => sum + creditGrantedFaceTotal(c), 0);
    const colSpan = onAdjustCredit || onCloseExpiredCredit ? 8 : 7;

    return (
        <Box>
            <Stack direction="row" alignItems="center" justifyContent="space-between" mb={0.75}>
                <Stack direction="row" alignItems="center" spacing={1} flexWrap="wrap" useFlexGap>
                    <CreditScoreIcon color="primary" sx={{ fontSize: 16 }} />
                    <Typography variant="subtitle2" sx={{ fontWeight: 600, fontSize: 12 * factor }}>Credits</Typography>
                    <Chip label={`Balance: ${formatCurrency(balanceTotal)}`} size="small" color="primary" sx={{ fontSize: 11 * factor }} />
                    <Chip label={`Issued: ${formatCurrency(issuedTotal)}`} size="small" color="success" sx={{ fontSize: 11 * factor }} />
                </Stack>
                <Button size="small" variant="outlined" startIcon={<AddIcon />} onClick={onAdd} sx={{ fontSize: 11 * factor }}>
                    Add Credit
                </Button>
            </Stack>

            {credits.length === 0 ? (
                <Typography variant="caption" color="text.secondary" sx={{ fontSize: 11 * factor }}>No credits on record.</Typography>
            ) : (
                <TableContainer>
                    <Table size="small" sx={{ '& th, & td': { fontSize: 11 * factor } }}>
                        <TableHead>
                            <TableRow>
                                <TableCell padding="checkbox" sx={{ width: 44 }} />
                                <TableCell>Description</TableCell>
                                <TableCell align="right">Total</TableCell>
                                <TableCell align="right">Remaining</TableCell>
                                <TableCell>Issued</TableCell>
                                <TableCell>Expires</TableCell>
                                <TableCell>Status</TableCell>
                                {onAdjustCredit || onCloseExpiredCredit ? <TableCell align="right" /> : null}
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {credits.map(c => {
                                const isOpen = !!expanded[c.creditID];
                                const txs: CreditTransaction[] = [...(c.transactions ?? [])].sort(
                                    (a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime(),
                                );
                                return (
                                    <Fragment key={c.creditID}>
                                        <TableRow hover>
                                            <TableCell padding="checkbox" sx={{ verticalAlign: 'middle' }}>
                                                <Tooltip title={isOpen ? 'Hide transactions & notes' : 'Show transactions & notes'}>
                                                    <IconButton
                                                        size="small"
                                                        onClick={() => toggleExpand(c.creditID)}
                                                        aria-expanded={isOpen}
                                                    >
                                                        {isOpen
                                                            ? <KeyboardArrowUpIcon sx={{ fontSize: 18 }} />
                                                            : <KeyboardArrowDownIcon sx={{ fontSize: 18 }} />
                                                        }
                                                    </IconButton>
                                                </Tooltip>
                                            </TableCell>
                                            <TableCell>{c.description}</TableCell>
                                            <TableCell align="right">{formatCurrency(creditGrantedFaceTotal(c))}</TableCell>
                                            <TableCell align="right" sx={{ fontWeight: 600 }}>{formatCurrency(c.remainingAmount)}</TableCell>
                                            <TableCell>{formatDate(c.issuedAt)}</TableCell>
                                            <TableCell>{formatDate(c.expiresAt)}</TableCell>
                                            <TableCell>
                                                <Chip
                                                    label={c.isActive ? 'Active' : 'Inactive'}
                                                    size="small"
                                                    color={c.isActive ? 'success' : 'default'}
                                                    sx={{ fontSize: 10 * factor }}
                                                />
                                            </TableCell>
                                            {onAdjustCredit || onCloseExpiredCredit ? (
                                                <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
                                                    {onCloseExpiredCredit &&
                                                    c.remainingAmount > 0 &&
                                                    c.expiresAt != null &&
                                                    isLikelyExpiredForCloseout(c.expiresAt) ? (
                                                        <Tooltip title="Close out expired credit (write off remaining balance)">
                                                            <IconButton size="small" onClick={() => onCloseExpiredCredit(c)}>
                                                                <ArchiveIcon sx={{ fontSize: 16 }} />
                                                            </IconButton>
                                                        </Tooltip>
                                                    ) : null}
                                                    {onAdjustCredit ? (
                                                        <Tooltip title="Adjust total & remaining">
                                                            <IconButton size="small" onClick={() => onAdjustCredit(c)}>
                                                                <TuneIcon sx={{ fontSize: 16 }} />
                                                            </IconButton>
                                                        </Tooltip>
                                                    ) : null}
                                                </TableCell>
                                            ) : null}
                                        </TableRow>
                                        <TableRow>
                                            <TableCell
                                                colSpan={colSpan}
                                                sx={{
                                                    py: 0,
                                                    borderBottom: isOpen ? undefined : 'none',
                                                    backgroundColor: 'action.hover',
                                                }}
                                            >
                                                <Collapse in={isOpen} timeout="auto" unmountOnExit>
                                                    <Box sx={{ py: 1.25, px: 1 }}>
                                                        <Typography
                                                            variant="caption"
                                                            color="text.secondary"
                                                            sx={{
                                                                fontSize: 10 * factor,
                                                                fontWeight: 700,
                                                                textTransform: 'uppercase',
                                                                letterSpacing: 0.8,
                                                                display: 'block',
                                                                mb: 0.75,
                                                            }}
                                                        >
                                                            Transaction history (notes from each adjustment or issue)
                                                        </Typography>
                                                        {txs.length === 0 ? (
                                                            <Typography variant="caption" color="text.secondary" sx={{ fontSize: 11 * factor }}>
                                                                No transactions on record.
                                                            </Typography>
                                                        ) : (
                                                            <>
                                                                <Table size="small">
                                                                    <TableHead>
                                                                    <TableRow>
                                                                        <TableCell sx={{ fontSize: 10 * factor }}>When (UTC)</TableCell>
                                                                        <TableCell sx={{ fontSize: 10 * factor }}>Type</TableCell>
                                                                        <TableCell align="right" sx={{ fontSize: 10 * factor }}>Amount</TableCell>
                                                                        <TableCell sx={{ fontSize: 10 * factor }}>Note</TableCell>
                                                                        <TableCell sx={{ fontSize: 10 * factor }}>By</TableCell>
                                                                        <TableCell align="center" sx={{ fontSize: 10 * factor, width: 44, px: 0.5 }}>
                                                                            <Tooltip title="Pending checkout record (MongoDB _id; links to Stripe via sessionId)">
                                                                                <ReceiptLongIcon sx={{ fontSize: 14 * factor, opacity: 0.45, display: 'block', mx: 'auto' }} />
                                                                            </Tooltip>
                                                                        </TableCell>
                                                                    </TableRow>
                                                                </TableHead>
                                                                <TableBody>
                                                                    {txs.map(tx => (
                                                                        <TableRow key={tx.transactionID}>
                                                                            <TableCell sx={{ whiteSpace: 'nowrap' }}>
                                                                                {formatDateTimeUTC(tx.timestamp)}
                                                                            </TableCell>
                                                                            <TableCell>
                                                                                <Chip
                                                                                    label={tx.type}
                                                                                    size="small"
                                                                                    color={CREDIT_TX_CHIP_COLOR[tx.type]}
                                                                                    sx={{ fontSize: 9 * factor, textTransform: 'capitalize' }}
                                                                                />
                                                                            </TableCell>
                                                                            <TableCell align="right" sx={{ fontWeight: 600 }}>
                                                                                {formatCurrency(tx.amount)}
                                                                            </TableCell>
                                                                            <TableCell>{tx.description || '—'}</TableCell>
                                                                            <TableCell>{tx.createdBy || '—'}</TableCell>
                                                                            <TableCell align="center" sx={{ px: 0.5 }}>
                                                                                {tx.type === 'redeemed' && tx.pendingCheckoutId ? (
                                                                                    <Tooltip
                                                                                        title={`Pending checkout _id: ${tx.pendingCheckoutId}`}
                                                                                    >
                                                                                        <IconButton
                                                                                            size="small"
                                                                                            aria-label="Pending checkout record id"
                                                                                            onClick={e => {
                                                                                                setCheckoutPopover({
                                                                                                    anchorEl: e.currentTarget,
                                                                                                    pendingCheckoutId: tx.pendingCheckoutId!,
                                                                                                });
                                                                                            }}
                                                                                        >
                                                                                            <ReceiptLongIcon sx={{ fontSize: 16 * factor }} />
                                                                                        </IconButton>
                                                                                    </Tooltip>
                                                                                ) : (
                                                                                    <Typography variant="caption" color="text.disabled" sx={{ fontSize: 9 * factor }}>—</Typography>
                                                                                )}
                                                                            </TableCell>
                                                                        </TableRow>
                                                                    ))}
                                                                </TableBody>
                                                            </Table>
                                                            <Popover
                                                                open={checkoutPopover != null}
                                                                anchorEl={checkoutPopover?.anchorEl ?? null}
                                                                onClose={() => setCheckoutPopover(null)}
                                                                anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
                                                                transformOrigin={{ vertical: 'top', horizontal: 'center' }}
                                                            >
                                                                <Box sx={{ p: 2, maxWidth: 380 }}>
                                                                    <Typography
                                                                        variant="caption"
                                                                        color="text.secondary"
                                                                        display="block"
                                                                        sx={{ mb: 1 }}
                                                                    >
                                                                        PendingCheckouts document _id (lookup checkout / Stripe sessionId)
                                                                    </Typography>
                                                                    <Typography
                                                                        variant="body2"
                                                                        sx={{ fontFamily: 'monospace', wordBreak: 'break-all', fontSize: 11 * factor }}
                                                                    >
                                                                        {checkoutPopover?.pendingCheckoutId ?? ''}
                                                                    </Typography>
                                                                </Box>
                                                            </Popover>
                                                            </>
                                                        )}
                                                    </Box>
                                                </Collapse>
                                            </TableCell>
                                        </TableRow>
                                    </Fragment>
                                );
                            })}
                        </TableBody>
                    </Table>
                </TableContainer>
            )}
        </Box>
    );
};

// ── Discounts sub-panel ──────────────────────────────────────────────────────
const DiscountsPanel = ({
    discounts, factor, onAdd, onEdit, onArchive,
}: {
    discounts: DiscountEntry[];
    factor: number;
    onAdd: () => void;
    onEdit: (d: DiscountEntry) => void;
    onArchive: (d: DiscountEntry) => void;
}) => (
    <Box>
        <Stack direction="row" alignItems="center" justifyContent="space-between" mb={0.75}>
            <Stack direction="row" alignItems="center" spacing={1}>
                <LocalOfferIcon color="primary" sx={{ fontSize: 16 }} />
                <Typography variant="subtitle2" sx={{ fontWeight: 600, fontSize: 12 * factor }}>Discounts</Typography>
            </Stack>
            <Button size="small" variant="outlined" color="primary" startIcon={<AddIcon />} onClick={onAdd} sx={{ fontSize: 11 * factor }}>
                Add Discount
            </Button>
        </Stack>

        {discounts.length === 0 ? (
            <Typography variant="caption" color="text.secondary" sx={{ fontSize: 11 * factor }}>No discounts on record.</Typography>
        ) : (
            <TableContainer>
                <Table size="small" sx={{ '& th, & td': { fontSize: 11 * factor } }}>
                    <TableHead>
                        <TableRow>
                            <TableCell>Description</TableCell>
                            <TableCell>Type</TableCell>
                            <TableCell align="right">Value</TableCell>
                            <TableCell>Start</TableCell>
                            <TableCell>End</TableCell>
                            <TableCell>Used / Limit</TableCell>
                            <TableCell>Req. Owner</TableCell>
                            <TableCell>Status</TableCell>
                            <TableCell />
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {discounts.map(d => (
                            <TableRow key={d.discountID} hover sx={{ opacity: d.isActive ? 1 : 0.55 }}>
                                <TableCell>{d.description}</TableCell>
                                <TableCell>
                                    <Chip
                                        label={d.discountType === 'percentage' ? '%' : '$'}
                                        size="small"
                                        variant="outlined"
                                        sx={{ fontSize: 10 * factor }}
                                    />
                                </TableCell>
                                <TableCell align="right" sx={{ fontWeight: 600 }}>
                                    {d.discountType === 'percentage' ? `${d.value}%` : formatCurrency(d.value)}
                                </TableCell>
                                <TableCell>{formatDate(d.startDate)}</TableCell>
                                <TableCell>{formatDate(d.endDate)}</TableCell>
                                <TableCell>{d.usageCount} / {d.usageLimit ?? '∞'}</TableCell>
                                <TableCell>
                                    <Chip
                                        label={d.requireOwner ? 'Yes' : 'No'}
                                        size="small"
                                        color={d.requireOwner ? 'warning' : 'default'}
                                        sx={{ fontSize: 10 * factor }}
                                    />
                                </TableCell>
                                <TableCell>
                                    <Chip
                                        label={d.isActive ? 'Active' : 'Archived'}
                                        size="small"
                                        color={d.isActive ? 'success' : 'default'}
                                        sx={{ fontSize: 10 * factor }}
                                    />
                                </TableCell>
                                <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
                                    <Tooltip title="Edit">
                                        <IconButton size="small" onClick={() => onEdit(d)}>
                                            <EditIcon sx={{ fontSize: 14 }} />
                                        </IconButton>
                                    </Tooltip>
                                    <Tooltip title={d.isActive ? 'Archive' : 'Restore'}>
                                        <IconButton size="small" onClick={() => onArchive(d)}>
                                            {d.isActive
                                                ? <ArchiveIcon sx={{ fontSize: 14 }} />
                                                : <UnarchiveIcon sx={{ fontSize: 14 }} />
                                            }
                                        </IconButton>
                                    </Tooltip>
                                </TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            </TableContainer>
        )}
    </Box>
);

// ── Bucket accordion ─────────────────────────────────────────────────────────
const BucketAccordion = ({
    title, bucket, factor, onAddCredit, onAddDiscount, onEditDiscount, onArchiveDiscount, onAdjustCredit, onCloseExpiredCredit,
}: {
    title: string;
    bucket: CreditDiscountBucket;
    factor: number;
    onAddCredit: () => void;
    onAddDiscount: () => void;
    onEditDiscount: (d: DiscountEntry) => void;
    onArchiveDiscount: (d: DiscountEntry) => void;
    onAdjustCredit?: (entry: CreditEntry) => void;
    onCloseExpiredCredit?: (entry: CreditEntry) => void;
}) => (
    <Accordion defaultExpanded sx={{ mb: 1 }}>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Typography variant="subtitle2" sx={{ fontWeight: 600, fontSize: 12 * factor }}>{title}</Typography>
        </AccordionSummary>
        <AccordionDetails>
            <CreditsPanel
                credits={bucket.credits}
                factor={factor}
                onAdd={onAddCredit}
                onAdjustCredit={onAdjustCredit}
                onCloseExpiredCredit={onCloseExpiredCredit}
            />
            <Divider sx={{ my: 1.5 }} />
            <DiscountsPanel
                discounts={bucket.discounts}
                factor={factor}
                onAdd={onAddDiscount}
                onEdit={onEditDiscount}
                onArchive={onArchiveDiscount}
            />
        </AccordionDetails>
    </Accordion>
);

// ── Add Credit Dialog ────────────────────────────────────────────────────────
interface AddCreditDialogProps {
    open: boolean;
    scope: Scope;
    tourID?: string;
    tourName?: string;
    factor: number;
    appTheme: any;
    onClose: () => void;
    onSave: (payload: Omit<AddCreditPayload, 'userID' | 'createdBy'>) => Promise<void>;
}

const AddCreditDialog = ({ open, scope, tourID, tourName, factor, appTheme, onClose, onSave }: AddCreditDialogProps) => {
    const f = factor < 1 ? factor : 1;
    const [amount, setAmount] = useState('');
    const [description, setDescription] = useState('');
    const [expiresAt, setExpiresAt] = useState('');
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const reset = () => {
        setAmount(''); setDescription(''); setExpiresAt(''); setError(null);
    };

    const handleClose = () => { reset(); onClose(); };

    const handleSave = async () => {
        if (!amount || isNaN(Number(amount)) || Number(amount) <= 0) {
            setError('Enter a valid dollar amount greater than 0.');
            return;
        }
        setSaving(true);
        setError(null);
        try {
            await onSave({
                scope,
                tourID: scope === 'tour' ? tourID : undefined,
                tourName: scope === 'tour' ? tourName : undefined,
                totalAmount: Number(amount),
                description,
                expiresAt: expiresAt || undefined,
            });
            reset();
        } catch {
            setError('Failed to add credit. Please try again.');
        } finally {
            setSaving(false);
        }
    };

    const title = scope === 'momentGolf' ? 'Moment Golf' : (tourName || 'Series');

    return (
        <Dialog open={open} onClose={handleClose} maxWidth="xs" fullWidth>
            <DialogTitle sx={{ fontSize: 14 * f }}>Add Credit — {title}</DialogTitle>
            <DialogContent>
                <Stack spacing={1.5} sx={{ mt: 2, mb: 1 }}>
                    {error && <Alert severity="error">{error}</Alert>}
                    <StyledTextboxInput
                        label="Amount ($)"
                        value={amount}
                        onChange={e => setAmount(e.target.value)}
                        type="number"
                        factor={f}
                        appTheme={appTheme}
                        fullWidth
                        inputProps={{ min: 0.01, step: 0.01 }}
                    />
                    <StyledTextboxInput
                        label="Description"
                        value={description}
                        onChange={e => setDescription(e.target.value)}
                        factor={f}
                        appTheme={appTheme}
                        fullWidth
                    />
                    <StyledTextboxInput
                        label="Expiry Date (optional)"
                        value={expiresAt}
                        onChange={e => setExpiresAt(e.target.value)}
                        type="date"
                        factor={f}
                        appTheme={appTheme}
                        fullWidth
                    />
                </Stack>
            </DialogContent>
            <DialogActions>
                <Button onClick={handleClose} disabled={saving} color="inherit">Cancel</Button>
                <Button onClick={handleSave} disabled={saving} variant="contained">
                    {saving ? <CircularProgress size={16} color="inherit" /> : 'Add Credit'}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

// ── Adjust Credit Dialog ───────────────────────────────────────────────────────
interface AdjustCreditDialogProps {
    open: boolean;
    credit: CreditEntry | null;
    scope: Scope;
    tourID?: string;
    tourName?: string;
    factor: number;
    appTheme: any;
    onClose: () => void;
    onSave: (payload: Omit<AdjustCreditPayload, 'userID' | 'createdBy'>) => Promise<void>;
}

const AdjustCreditDialog = ({
    open, credit, scope, tourID, tourName, factor, appTheme, onClose, onSave,
}: AdjustCreditDialogProps) => {
    const f = factor < 1 ? factor : 1;
    const [delta, setDelta] = useState('');
    const [description, setDescription] = useState('');
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const reset = () => { setDelta(''); setDescription(''); setError(null); };

    const handleClose = () => {
        reset();
        onClose();
    };

    const handleSave = async () => {
        if (!credit) return;
        const raw = delta.trim();
        if (!raw || isNaN(Number(raw)) || Number(raw) === 0) {
            setError('Enter a non-zero adjustment amount (negative reduces total and remaining).');
            return;
        }
        setSaving(true);
        setError(null);
        try {
            await onSave({
                scope,
                tourID: scope === 'tour' ? tourID : undefined,
                creditID: credit.creditID,
                amount: Number(raw),
                description: description.trim() || 'Credit adjustment',
            });
            reset();
            onClose();
        } catch {
            setError('Failed to apply adjustment. Check that total and remaining cannot go negative.');
        } finally {
            setSaving(false);
        }
    };

    const bucketTitle = scope === 'momentGolf' ? 'Moment Golf' : (tourName || 'Series');

    return (
        <Dialog open={open} onClose={handleClose} maxWidth="xs" fullWidth>
            <DialogTitle sx={{ fontSize: 14 * f }}>Adjust Credit — {bucketTitle}</DialogTitle>
            <DialogContent>
                {credit && (
                    <Stack spacing={1.5} sx={{ mt: 2, mb: 1 }}>
                        {error && <Alert severity="error">{error}</Alert>}
                        <Typography variant="caption" color="text.secondary" sx={{ fontSize: 11 * f }}>
                            {credit.description || '—'} · issued {formatCurrency(creditGrantedFaceTotal(credit))} · remaining{' '}
                            {formatCurrency(credit.remainingAmount)}
                        </Typography>
                        <StyledTextboxInput
                            label="Adjustment ($)"
                            value={delta}
                            onChange={e => setDelta(e.target.value)}
                            type="number"
                            factor={f}
                            appTheme={appTheme}
                            fullWidth
                            inputProps={{ step: 0.01 }}
                            helperText="Positive increases total and remaining; negative decreases both."
                        />
                        <StyledTextboxInput
                            label="Note (optional)"
                            value={description}
                            onChange={e => setDescription(e.target.value)}
                            factor={f}
                            appTheme={appTheme}
                            fullWidth
                        />
                    </Stack>
                )}
            </DialogContent>
            <DialogActions>
                <Button onClick={handleClose} disabled={saving} color="inherit">Cancel</Button>
                <Button onClick={handleSave} disabled={saving || !credit} variant="contained" color="primary">
                    {saving ? <CircularProgress size={16} color="inherit" /> : 'Apply'}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

// ── Add Discount Dialog ───────────────────────────────────────────────────────
interface AddDiscountDialogProps {
    open: boolean;
    scope: Scope;
    tourID?: string;
    tourName?: string;
    factor: number;
    appTheme: any;
    onClose: () => void;
    onSave: (payload: Omit<AddDiscountPayload, 'userID' | 'createdBy'>) => Promise<void>;
}

const AddDiscountDialog = ({ open, scope, tourID, tourName, factor, appTheme, onClose, onSave }: AddDiscountDialogProps) => {
    const f = factor < 1 ? factor : 1;
    const [discountType, setDiscountType] = useState<'percentage' | 'dollar'>('dollar');
    const [value, setValue] = useState('');
    const [description, setDescription] = useState('');
    const [startDate, setStartDate] = useState('');
    const [endDate, setEndDate] = useState('');
    const [usageLimit, setUsageLimit] = useState('');
    const [requireOwner, setRequireOwner] = useState(false);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const reset = () => {
        setDiscountType('dollar'); setValue(''); setDescription('');
        setStartDate(''); setEndDate(''); setUsageLimit('');
        setRequireOwner(false); setError(null);
    };

    const handleClose = () => { reset(); onClose(); };

    const handleSave = async () => {
        if (!value || isNaN(Number(value)) || Number(value) <= 0) {
            setError('Enter a valid discount value greater than 0.');
            return;
        }
        if (discountType === 'percentage' && Number(value) > 100) {
            setError('Percentage discount cannot exceed 100%.');
            return;
        }
        if (!startDate || !endDate) {
            setError('Start date and end date are required.');
            return;
        }
        if (new Date(endDate) <= new Date(startDate)) {
            setError('End date must be after start date.');
            return;
        }
        setSaving(true);
        setError(null);
        try {
            await onSave({
                scope,
                tourID: scope === 'tour' ? tourID : undefined,
                tourName: scope === 'tour' ? tourName : undefined,
                discountType,
                value: Number(value),
                description,
                startDate,
                endDate,
                usageLimit: usageLimit ? Number(usageLimit) : undefined,
                requireOwner,
            });
            reset();
        } catch {
            setError('Failed to add discount. Please try again.');
        } finally {
            setSaving(false);
        }
    };

    const title = scope === 'momentGolf' ? 'Moment Golf' : (tourName || 'Series');

    return (
        <Dialog open={open} onClose={handleClose} maxWidth="xs" fullWidth>
            <DialogTitle sx={{ fontSize: 14 * f }}>Add Discount — {title}</DialogTitle>
            <DialogContent>
                <Stack spacing={1.5} sx={{ mt: 2, mb: 1 }}>
                    {error && <Alert severity="error">{error}</Alert>}

                    <Stack direction="column" spacing={0.5}>
                        <Typography variant="caption" color="text.secondary" sx={{ fontSize: 10 * f }}>Discount Type</Typography>
                        <ToggleButtonGroup
                            value={discountType}
                            exclusive
                            color="primary"
                            onChange={(_, val) => { if (val) setDiscountType(val); }}
                            size="small"
                        >
                            <ToggleButton value="dollar" sx={{ fontSize: 11 * f }}>Dollar ($)</ToggleButton>
                            <ToggleButton value="percentage" sx={{ fontSize: 11 * f }}>Percentage (%)</ToggleButton>
                        </ToggleButtonGroup>
                    </Stack>

                    <StyledTextboxInput
                        label={discountType === 'percentage' ? 'Discount (%)' : 'Discount ($)'}
                        value={value}
                        onChange={e => setValue(e.target.value)}
                        type="number"
                        factor={f}
                        appTheme={appTheme}
                        fullWidth
                        inputProps={{ min: 0.01, step: discountType === 'percentage' ? 1 : 0.01, max: discountType === 'percentage' ? 100 : undefined }}
                    />
                    <StyledTextboxInput
                        label="Description"
                        value={description}
                        onChange={e => setDescription(e.target.value)}
                        factor={f}
                        appTheme={appTheme}
                        fullWidth
                    />
                    <Stack direction="row" spacing={1}>
                        <StyledTextboxInput
                            label="Start Date"
                            value={startDate}
                            onChange={e => setStartDate(e.target.value)}
                            type="date"
                            factor={f}
                            appTheme={appTheme}
                            sx={{ flex: 1 }}
                        />
                        <StyledTextboxInput
                            label="End Date"
                            value={endDate}
                            onChange={e => setEndDate(e.target.value)}
                            type="date"
                            factor={f}
                            appTheme={appTheme}
                            sx={{ flex: 1 }}
                        />
                    </Stack>
                    <StyledTextboxInput
                        label="Usage Limit (optional)"
                        value={usageLimit}
                        onChange={e => setUsageLimit(e.target.value)}
                        type="number"
                        factor={f}
                        appTheme={appTheme}
                        fullWidth
                        helperText="Leave blank for unlimited use"
                        inputProps={{ min: 1 }}
                    />
                    <FormControlLabel
                        control={
                            <Switch
                                checked={requireOwner}
                                onChange={e => setRequireOwner(e.target.checked)}
                                size="small"
                            />
                        }
                        label={
                            <Typography variant="caption" sx={{ fontSize: 11 * f }}>
                                Require owner to apply
                            </Typography>
                        }
                    />
                </Stack>
            </DialogContent>
            <DialogActions>
                <Button onClick={handleClose} disabled={saving} color="inherit">Cancel</Button>
                <Button onClick={handleSave} disabled={saving} variant="contained" color="primary">
                    {saving ? <CircularProgress size={16} color="inherit" /> : 'Add Discount'}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

// ── Edit Discount Dialog ──────────────────────────────────────────────────────
interface EditDiscountDialogProps {
    open: boolean;
    discount: DiscountEntry | null;
    factor: number;
    appTheme: any;
    onClose: () => void;
    onSave: (payload: Omit<UpdateDiscountPayload, 'userID' | 'modifiedBy' | 'scope' | 'tourID' | 'discountID'>) => Promise<void>;
}

const EditDiscountDialog = ({ open, discount, factor, appTheme, onClose, onSave }: EditDiscountDialogProps) => {
    const f = factor < 1 ? factor : 1;
    const [discountType, setDiscountType] = useState<'percentage' | 'dollar'>('dollar');
    const [value, setValue] = useState('');
    const [description, setDescription] = useState('');
    const [startDate, setStartDate] = useState('');
    const [endDate, setEndDate] = useState('');
    const [usageLimit, setUsageLimit] = useState('');
    const [requireOwner, setRequireOwner] = useState(false);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState<string | null>(null);

    // Populate fields when the target discount changes
    React.useEffect(() => {
        if (discount) {
            setDiscountType(discount.discountType);
            setValue(String(discount.value));
            setDescription(discount.description);
            setStartDate(toUTCDateInput(discount.startDate));
            setEndDate(toUTCDateInput(discount.endDate));
            setUsageLimit(discount.usageLimit != null ? String(discount.usageLimit) : '');
            setRequireOwner(discount.requireOwner ?? false);
            setError(null);
        }
    }, [discount]);

    const handleClose = () => { setError(null); onClose(); };

    const handleSave = async () => {
        if (!value || isNaN(Number(value)) || Number(value) <= 0) {
            setError('Enter a valid discount value greater than 0.');
            return;
        }
        if (discountType === 'percentage' && Number(value) > 100) {
            setError('Percentage discount cannot exceed 100%.');
            return;
        }
        if (!startDate || !endDate) {
            setError('Start date and end date are required.');
            return;
        }
        if (new Date(endDate) <= new Date(startDate)) {
            setError('End date must be after start date.');
            return;
        }
        setSaving(true);
        setError(null);
        try {
            await onSave({
                discountType,
                value: Number(value),
                description,
                startDate,
                endDate,
                usageLimit: usageLimit ? Number(usageLimit) : null,
                requireOwner,
            });
            onClose();
        } catch {
            setError('Failed to save changes. Please try again.');
        } finally {
            setSaving(false);
        }
    };

    return (
        <Dialog open={open} onClose={handleClose} maxWidth="xs" fullWidth>
            <DialogTitle sx={{ fontSize: 14 * f }}>Edit Discount</DialogTitle>
            <DialogContent>
                <Stack spacing={1.5} mt={0.5}>
                    {error && <Alert severity="error">{error}</Alert>}

                    <Stack direction="column" spacing={0.5}>
                        <Typography variant="caption" color="text.secondary" sx={{ fontSize: 10 * f }}>Discount Type</Typography>
                        <ToggleButtonGroup
                            value={discountType}
                            exclusive
                            color="primary"
                            onChange={(_, val) => { if (val) setDiscountType(val); }}
                            size="small"
                        >
                            <ToggleButton value="dollar" sx={{ fontSize: 11 * f }}>Dollar ($)</ToggleButton>
                            <ToggleButton value="percentage" sx={{ fontSize: 11 * f }}>Percentage (%)</ToggleButton>
                        </ToggleButtonGroup>
                    </Stack>

                    <StyledTextboxInput
                        label={discountType === 'percentage' ? 'Discount (%)' : 'Discount ($)'}
                        value={value}
                        onChange={e => setValue(e.target.value)}
                        type="number"
                        factor={f}
                        appTheme={appTheme}
                        fullWidth
                        inputProps={{ min: 0.01, step: discountType === 'percentage' ? 1 : 0.01, max: discountType === 'percentage' ? 100 : undefined }}
                    />
                    <StyledTextboxInput
                        label="Description"
                        value={description}
                        onChange={e => setDescription(e.target.value)}
                        factor={f}
                        appTheme={appTheme}
                        fullWidth
                    />
                    <Stack direction="row" spacing={1}>
                        <StyledTextboxInput
                            label="Start Date"
                            value={startDate}
                            onChange={e => setStartDate(e.target.value)}
                            type="date"
                            factor={f}
                            appTheme={appTheme}
                            sx={{ flex: 1 }}
                        />
                        <StyledTextboxInput
                            label="End Date"
                            value={endDate}
                            onChange={e => setEndDate(e.target.value)}
                            type="date"
                            factor={f}
                            appTheme={appTheme}
                            sx={{ flex: 1 }}
                        />
                    </Stack>
                    <StyledTextboxInput
                        label="Usage Limit (optional)"
                        value={usageLimit}
                        onChange={e => setUsageLimit(e.target.value)}
                        type="number"
                        factor={f}
                        appTheme={appTheme}
                        fullWidth
                        helperText="Leave blank for unlimited use"
                        inputProps={{ min: 1 }}
                    />
                    <FormControlLabel
                        control={
                            <Switch
                                checked={requireOwner}
                                onChange={e => setRequireOwner(e.target.checked)}
                                size="small"
                            />
                        }
                        label={
                            <Typography variant="caption" sx={{ fontSize: 11 * f }}>
                                Require owner to apply
                            </Typography>
                        }
                    />
                </Stack>
            </DialogContent>
            <DialogActions>
                <Button onClick={handleClose} disabled={saving} color="inherit">Cancel</Button>
                <Button onClick={handleSave} disabled={saving} variant="contained" color="primary">
                    {saving ? <CircularProgress size={16} color="inherit" /> : 'Save Changes'}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

// ── Main Section ─────────────────────────────────────────────────────────────
export const CreditsDiscountsSection = ({ appDims, compHeights }: CreditsDiscountsSectionProps) => {
    const { factor, appTheme, selectedUser } = useTBContext();
    const api = CreateApi();
    const auth = useAuth();

    const [data, setData] = useState<UserCreditsDiscounts | null>(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const [creditDialog, setCreditDialog] = useState<{ open: boolean; scope: Scope; tourID?: string; tourName?: string }>({ open: false, scope: 'momentGolf' });
    const [adjustCreditDialog, setAdjustCreditDialog] = useState<{
        open: boolean;
        scope: Scope;
        tourID?: string;
        tourName?: string;
        credit: CreditEntry | null;
    }>({ open: false, scope: 'momentGolf', credit: null });
    const [discountDialog, setDiscountDialog] = useState<{ open: boolean; scope: Scope; tourID?: string; tourName?: string }>({ open: false, scope: 'momentGolf' });
    const [editDialog, setEditDialog] = useState<{ open: boolean; discount: DiscountEntry | null; scope: Scope; tourID?: string }>({ open: false, discount: null, scope: 'momentGolf' });

    const f = factor < 1 ? factor : 1;

    const fetchData = useCallback(() => {
        if (!selectedUser) return;
        setLoading(true);
        setError(null);
        api.get(`/um/GetCreditsDiscounts?userID=${selectedUser.userID}`)
            .then(res => setData(res.data))
            .catch(() => setError('Failed to load credits & discounts.'))
            .finally(() => setLoading(false));
    }, [selectedUser?.userID]);

    useEffect(() => { fetchData(); }, [fetchData]);

    const handleAddCredit = useCallback(async (payload: Omit<AddCreditPayload, 'userID' | 'createdBy'>) => {
        await api.post('/um/AddCredit', {
            ...payload,
            userID: selectedUser!.userID,
            createdBy: auth.user?.profile?.sub ?? 'admin',
        });
        fetchData();
    }, [api, auth, fetchData, selectedUser]);

    const handleAdjustCredit = useCallback(async (payload: Omit<AdjustCreditPayload, 'userID' | 'createdBy'>) => {
        await api.post('/um/AdjustCredit', {
            ...payload,
            userID: selectedUser!.userID,
            createdBy: auth.user?.profile?.sub ?? 'admin',
        });
        fetchData();
    }, [api, auth, fetchData, selectedUser]);

    const handleAddDiscount = useCallback(async (payload: Omit<AddDiscountPayload, 'userID' | 'createdBy'>) => {
        await api.post('/um/AddDiscount', {
            ...payload,
            userID: selectedUser!.userID,
            createdBy: auth.user?.profile?.sub ?? 'admin',
        });
        fetchData();
    }, [api, auth, fetchData, selectedUser]);

    const handleEditDiscount = useCallback(async (
        fields: Omit<UpdateDiscountPayload, 'userID' | 'modifiedBy' | 'scope' | 'tourID' | 'discountID'>
    ) => {
        const { discount, scope, tourID } = editDialog;
        if (!discount) return;
        await api.put('/um/UpdateDiscount', {
            ...fields,
            userID: selectedUser!.userID,
            discountID: discount.discountID,
            scope,
            tourID,
            modifiedBy: auth.user?.profile?.sub ?? 'admin',
        } as UpdateDiscountPayload);
        fetchData();
    }, [api, auth, editDialog, fetchData, selectedUser]);

    const handleArchiveDiscount = useCallback(async (discount: DiscountEntry, scope: Scope, tourID?: string) => {
        await api.put('/um/UpdateDiscount', {
            userID: selectedUser!.userID,
            discountID: discount.discountID,
            scope,
            tourID,
            isActive: !discount.isActive,
            modifiedBy: auth.user?.profile?.sub ?? 'admin',
        } as UpdateDiscountPayload);
        fetchData();
    }, [api, auth, fetchData, selectedUser]);

    const handleCloseExpiredCredit = useCallback(async (credit: CreditEntry, scope: Scope, tourID?: string) => {
        await api.post('/um/CloseExpiredCredit', {
            userID: selectedUser!.userID,
            creditID: credit.creditID,
            scope,
            tourID: scope === 'tour' ? tourID : undefined,
            createdBy: auth.user?.profile?.sub ?? 'admin',
        } as CloseExpiredCreditPayload);
        fetchData();
    }, [api, auth, fetchData, selectedUser]);

    if (loading) {
        return (
            <Stack alignItems="center" justifyContent="center" sx={{ height: '100%', minHeight: 0 }}>
                <CircularProgress />
            </Stack>
        );
    }

    const mgBucket: CreditDiscountBucket = data?.momentGolf ?? EMPTY_BUCKET;

    // Merge profile tours with loaded data — always show a bucket for every
    // series on the user's profile, even when no credits or discounts exist yet.
    const profileTours = selectedUser?.tours ?? [];
    const tourBuckets: TourCreditDiscountBucket[] = profileTours.map(t => {
        const existing = (data?.tour ?? []).find(b => b.tourID === t.tourID);
        return existing ?? {
            tourID: t.tourID,
            tourName: t.tourName,
            credits: [],
            discounts: [],
        };
    });

    return (
        <Box
            sx={{
                height: '100%',
                minHeight: 0,
                display: 'flex',
                flexDirection: 'column',
                overflow: 'hidden',
                px: 2,
                py: 1.5,
                boxSizing: 'border-box',
            }}
        >
            <Box sx={{ flexShrink: 0, mb: 1.5 }}>
                <Typography variant="subtitle1" color="primary.main" sx={{ fontWeight: 600, fontSize: 14 * f }}>
                    Credits & Discounts
                </Typography>
                {error && <Alert severity="error" sx={{ mt: 1.5 }}>{error}</Alert>}
            </Box>

            <Box sx={{ flex: 1, minHeight: 0, overflowY: 'auto' }}>
                <SectionLabel label="Moment Golf" factor={f} />
                <Box mt={0.75} mb={2.5}>
                    <BucketAccordion
                        title="Moment Golf Platform"
                        bucket={mgBucket}
                        factor={f}
                        onAddCredit={() => setCreditDialog({ open: true, scope: 'momentGolf' })}
                        onAddDiscount={() => setDiscountDialog({ open: true, scope: 'momentGolf' })}
                        onEditDiscount={d => setEditDialog({ open: true, discount: d, scope: 'momentGolf' })}
                        onArchiveDiscount={d => handleArchiveDiscount(d, 'momentGolf')}
                        onAdjustCredit={c => setAdjustCreditDialog({ open: true, scope: 'momentGolf', credit: c })}
                        onCloseExpiredCredit={c => handleCloseExpiredCredit(c, 'momentGolf')}
                    />
                </Box>

                <Divider sx={{ mb: 2 }} />

                <SectionLabel label="Series / Tour" factor={f} />

                <Box mt={0.75} sx={{ pb: 0.5 }}>
                    {tourBuckets.length === 0 ? (
                        <Paper variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
                            <Typography variant="body2" color="text.secondary" sx={{ fontSize: 12 * f }}>
                                No series associations on this account. Add a series from the User Profile tab.
                            </Typography>
                        </Paper>
                    ) : (
                        tourBuckets.map(bucket => (
                            <BucketAccordion
                                key={bucket.tourID}
                                title={bucket.tourName || bucket.tourID}
                                bucket={bucket}
                                factor={f}
                                onAddCredit={() => setCreditDialog({ open: true, scope: 'tour', tourID: bucket.tourID, tourName: bucket.tourName })}
                                onAddDiscount={() => setDiscountDialog({ open: true, scope: 'tour', tourID: bucket.tourID, tourName: bucket.tourName })}
                                onEditDiscount={d => setEditDialog({ open: true, discount: d, scope: 'tour', tourID: bucket.tourID })}
                                onArchiveDiscount={d => handleArchiveDiscount(d, 'tour', bucket.tourID)}
                                onAdjustCredit={c => setAdjustCreditDialog({
                                    open: true,
                                    scope: 'tour',
                                    tourID: bucket.tourID,
                                    tourName: bucket.tourName,
                                    credit: c,
                                })}
                                onCloseExpiredCredit={c => handleCloseExpiredCredit(c, 'tour', bucket.tourID)}
                            />
                        ))
                    )}
                </Box>
            </Box>

            <AddCreditDialog
                open={creditDialog.open}
                scope={creditDialog.scope}
                tourID={creditDialog.tourID}
                tourName={creditDialog.tourName}
                factor={f}
                appTheme={appTheme}
                onClose={() => setCreditDialog(d => ({ ...d, open: false }))}
                onSave={payload => handleAddCredit(payload)}
            />
            <AdjustCreditDialog
                open={adjustCreditDialog.open}
                credit={adjustCreditDialog.credit}
                scope={adjustCreditDialog.scope}
                tourID={adjustCreditDialog.tourID}
                tourName={adjustCreditDialog.tourName}
                factor={f}
                appTheme={appTheme}
                onClose={() => setAdjustCreditDialog(d => ({ ...d, open: false, credit: null }))}
                onSave={payload => handleAdjustCredit(payload)}
            />
            <AddDiscountDialog
                open={discountDialog.open}
                scope={discountDialog.scope}
                tourID={discountDialog.tourID}
                tourName={discountDialog.tourName}
                factor={f}
                appTheme={appTheme}
                onClose={() => setDiscountDialog(d => ({ ...d, open: false }))}
                onSave={payload => handleAddDiscount(payload)}
            />
            <EditDiscountDialog
                open={editDialog.open}
                discount={editDialog.discount}
                factor={f}
                appTheme={appTheme}
                onClose={() => setEditDialog(d => ({ ...d, open: false }))}
                onSave={fields => handleEditDiscount(fields)}
            />
        </Box>
    );
};
